import React, { useRef, useEffect, useState } from "react";
import { Box, TextField, Typography, Button } from "@mui/material";
import HeightIcon from '@mui/icons-material/Height';

type Props = {
  imageUrl: string;
  height: number | "";
  onHeightChange: (value: number | "") => void;
  onNextStep: () => void;
};

const HeightInputComponent: React.FC<Props> = ({
  imageUrl,
  height,
  onHeightChange,
  onNextStep,
}) => {
  const imageRef = useRef<HTMLImageElement>(null);
  const [arrowHeight, setArrowHeight] = useState<number>(0);

  useEffect(() => {
    const updateArrowHeight = () => {
      if (imageRef.current) {
        setArrowHeight(imageRef.current.clientHeight);
      }
    };

    const img = imageRef.current;
    if (img) {
      img.onload = updateArrowHeight;
    }

    window.addEventListener('resize', updateArrowHeight);
    updateArrowHeight();

    return () => {
      window.removeEventListener('resize', updateArrowHeight);
      if (img) {
        img.onload = null;
      }
    };
  }, [imageUrl]);

  return (
    <Box sx={{ 
      display: "flex", 
      flexDirection: "column",
      gap: 4,
      alignItems: "center",
      width: "100%"
    }}>
      {/* 上部：画像と矢印 */}
      <Box sx={{ 
        display: "flex",
        gap: 4,
        justifyContent: "center",
        width: "100%"
      }}>
        {/* 画像 */}
        <Box sx={{ 
          maxWidth: "500px",
          width: { xs: "70%", sm: "500px" }
        }}>
          <img 
            ref={imageRef}
            src={imageUrl} 
            alt="商品画像" 
            style={{ 
              width: "100%", 
              height: "auto",
              maxHeight: "500px",
              objectFit: "contain" 
            }} 
          />
        </Box>

        {/* 矢印 */}
        <Box sx={{ 
          display: "flex", 
          flexDirection: "column",
          alignItems: "center",
          color: "primary.main",
          height: arrowHeight || 'auto',
          justifyContent: "space-between"
        }}>
          <HeightIcon sx={{ fontSize: 40, transform: 'rotate(180deg)' }} />
          <Typography 
            sx={{ 
              writingMode: "vertical-rl",
              textOrientation: "mixed",
              fontSize: "1.2rem"
            }}
          >
            高さ
          </Typography>
          <HeightIcon sx={{ fontSize: 40 }} />
        </Box>
      </Box>

      {/* 下部：入力フォームとボタン */}
      <Box sx={{ 
        width: "100%",
        maxWidth: "500px",
        display: "flex",
        flexDirection: "column",
        gap: 2
      }}>
        <TextField
          label="高さ"
          type="number"
          value={height}
          onChange={(e) => onHeightChange(Number(e.target.value) || "")}
          fullWidth
          required
          InputProps={{
            endAdornment: <Typography>m</Typography>,
          }}
          inputProps={{
            step: "0.01",
            min: "0"
          }}
        />
        <Typography variant="caption" sx={{ mt: 1, display: "block" }}>
          商品の実際の高さをメートル単位で入力してください
        </Typography>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={onNextStep}
          disabled={!height} // 高さが入力されていない場合はボタンを無効化
          sx={{ mt: 2 }}
        >
          次へ進む
        </Button>
      </Box>
    </Box>
  );
};

export default HeightInputComponent; 